import React from 'react'
import PropTypes from 'prop-types'
import {Box, Button, HStack, Flex, useMultiStyleConfig} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import DisplayPrice from '../display-price'
import OmnibusPrice from '../display-price/omnibus-price'
import VatLabel from '../vat-label'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import EnergyLabel from '../energy-label'
import WeeeFee from '../weee-fee'

const PriceCard = ({
    priceData,
    product,
    activeCurrency,
    variant = '',
    showOmnibusPrice = true,
    placeInfoPopoverInOmnibus = false,
    showDisplayPriceInfoWithOmnibus = false,
    extractWeeeFromPrice = false,
    productDetailCurrentPriceTextStyle,
    productDetailListPriceTextStyle,
    energyEfficiencyLevel,
    energyManualDocument,
    weeeFeeAmount,
    onFocusLinkClick,
}) => {
    const styles = useMultiStyleConfig('PriceCard', {variant})
    const normalizedWeeeFee = Number(
        product?.c_weeeFee ?? product?.representedProduct?.c_weeeFee ?? priceData?.c_weeeFee,
    )
    const weeeFee = Number.isFinite(normalizedWeeeFee) ? normalizedWeeeFee : 0
    const isVATFlag = useBooleanCustomPreference('showVATFlag')
    const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
    const intl = useIntl()

    const priceDataToDisplay =
        extractWeeeFromPrice && weeeFee > 0
            ? {
                  ...priceData,
                  currentPrice: Math.max(0, Number(priceData?.currentPrice || 0) - weeeFee),
                  listPrice: Math.max(0, Number(priceData?.listPrice || 0) - weeeFee),
                  isOnSale:
                      Math.max(0, Number(priceData?.listPrice || 0) - weeeFee) >
                      Math.max(0, Number(priceData?.currentPrice || 0) - weeeFee),
              }
            : priceData

    const {isOnSale} = priceDataToDisplay || false
    return (
        <Box {...styles.container}>
            {!notShoppableCountry && (
                <HStack {...styles.priceBoxIva}>
                    <Flex {...styles.priceOmniVatContainer}>
                        <Box {...styles.priceBox}>
                            {priceDataToDisplay?.currentPrice ? (
                                <DisplayPrice
                                    priceData={priceDataToDisplay}
                                    currency={product?.currency || activeCurrency}
                                    productDetailPriceView={true}
                                    showDiscount={true}
                                    variant={variant}
                                    productDetailCurrentPriceTextStyle={
                                        productDetailCurrentPriceTextStyle
                                    }
                                    productDetailListPriceTextStyle={
                                        productDetailListPriceTextStyle
                                    }
                                    showPriceInfoPopover={
                                        !placeInfoPopoverInOmnibus ||
                                        showDisplayPriceInfoWithOmnibus
                                    }
                                />
                            ) : (
                                <></>
                            )}
                        </Box>

                        {isVATFlag && priceDataToDisplay ? <VatLabel /> : <></>}

                        {showOmnibusPrice && (
                            <OmnibusPrice
                                priceData={priceDataToDisplay}
                                currency={product?.currency || activeCurrency}
                                omnibusExpandedView={true}
                                showDiscount={true}
                                showInfoPopover={isOnSale && placeInfoPopoverInOmnibus}
                                variant={variant}
                            />
                        )}
                    </Flex>

                    {(energyEfficiencyLevel || weeeFeeAmount > 0) && (
                        <Box {...styles.energyLabel}>
                            <Flex {...styles.energyLabelContent} alignItems="center">
                                {weeeFeeAmount > 0 && (
                                    <WeeeFee price={weeeFeeAmount} variant="productView" />
                                )}
                                {energyEfficiencyLevel && (
                                    <>
                                        <EnergyLabel
                                            level={energyEfficiencyLevel}
                                            manualDocument={energyManualDocument}
                                            iconSize={'small'}
                                            width="42.036px"
                                            labelPosition="top"
                                        />
                                        <Button
                                            variant="link-button"
                                            size="base"
                                            onClick={() =>
                                                onFocusLinkClick?.('editorial-key-features')
                                            }
                                        >
                                            {intl.formatMessage({
                                                defaultMessage: 'Product information',
                                                id: 'energy_label.product_information',
                                            })}
                                        </Button>
                                    </>
                                )}
                            </Flex>
                        </Box>
                    )}
                </HStack>
            )}
        </Box>
    )
}

PriceCard.propTypes = {
    priceData: PropTypes.shape({
        currentPrice: PropTypes.number,
        listPrice: PropTypes.number,
        isOnSale: PropTypes.bool,
        c_weeeFee: PropTypes.number,
    }).isRequired,
    product: PropTypes.shape({
        currency: PropTypes.string,
        c_weeeFee: PropTypes.number,
        representedProduct: PropTypes.shape({
            c_weeeFee: PropTypes.number,
        }),
    }),
    activeCurrency: PropTypes.string.isRequired,
    variant: PropTypes.string,
    showOmnibusPrice: PropTypes.bool,
    placeInfoPopoverInOmnibus: PropTypes.bool,
    showDisplayPriceInfoWithOmnibus: PropTypes.bool,
    extractWeeeFromPrice: PropTypes.bool,
    productDetailCurrentPriceTextStyle: PropTypes.string,
    productDetailListPriceTextStyle: PropTypes.string,
    energyEfficiencyLevel: PropTypes.string,
    energyManualDocument: PropTypes.string,
    weeeFeeAmount: PropTypes.number,
    onFocusLinkClick: PropTypes.func,
}
export default PriceCard
